/**
 * 應用程式主元件
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SystemProvider } from './contexts/SystemContext';
import { AuthProvider } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { ToastProvider } from './contexts/ToastContext';
import { useSystem } from './contexts/SystemContext';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import MaintenancePage from './pages/MaintenancePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrganizationsPage from './pages/OrganizationsPage';
import SysProfilePage from './pages/SysProfilePage';
import UserRolesPage from './pages/UserRolesPage';
import UsersPage from './pages/UsersPage';
import SystemFunctionsPage from './pages/SystemFunctionsPage';
import RoleRightsPage from './pages/RoleRightsPage';
import UserLogsPage from './pages/UserLogsPage';
import SystemCodesPage from './pages/SystemCodesPage';
import SystemNotificationsPage from './pages/SystemNotificationsPage';
import FileAttachmentsPage from './pages/FileAttachmentsPage';
import TenantProfilePage from './pages/TenantProfilePage';
import TenantUsersPage from './pages/TenantUsersPage';
import HomePage from './pages/HomePage';
import MyProfilePage from './pages/MyProfilePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ProjectsPage from './pages/ProjectsPage';
import SectionA1Page from './pages/SectionA1Page';
import SectionA2Page from './pages/SectionA2Page';
import SectionA3Page from './pages/SectionA3Page';
import SectionA4Page from './pages/SectionA4Page';
import SectionA5Page from './pages/SectionA5Page';
import SectionA6Page from './pages/SectionA6Page';
import SectionB1Page from './pages/SectionB1Page';
import SectionB2Page from './pages/SectionB2Page';
import SectionB3Page from './pages/SectionB3Page';
import SectionB4Page from './pages/SectionB4Page';
import SectionB5Page from './pages/SectionB5Page';
import SectionB6Page from './pages/SectionB6Page';
import SectionB71Page from './pages/SectionB71Page';
import SectionB72Page from './pages/SectionB72Page';
import SectionB73Page from './pages/SectionB73Page';
import SectionB74Page from './pages/SectionB74Page';
import SectionB75Page from './pages/SectionB75Page';
import SectionB76Page from './pages/SectionB76Page';
import SectionB8Page from './pages/SectionB8Page';
import SectionCPage from './pages/SectionCPage';
import SectionDPage from './pages/SectionDPage';
import SectionEPage from './pages/SectionEPage';
import SectionFPage from './pages/SectionFPage';
import AppendixPage from './pages/AppendixPage';
import ReportPage from './pages/ReportPage';
import NmethProposalPage from './pages/NmethProposalPage';
import RptNmethProposalPage from './pages/RptNmethProposalPage';
import RptNmethPage from './pages/RptNmethPage';
import NmethInfoPage from './pages/NmethInfoPage';
import NsectionAPage from './pages/NsectionAPage';
import NsectionB1Page from './pages/NsectionB1Page';
import NsectionB2Page from './pages/NsectionB2Page';
import NsectionB3Page from './pages/NsectionB3Page';
import NsectionB4Page from './pages/NsectionB4Page';
import NsectionB5Page from './pages/NsectionB5Page';
import NsectionB6Page from './pages/NsectionB6Page';
import NsectionB7Page from './pages/NsectionB7Page';
import NsectionB8Page from './pages/NsectionB8Page';
import NsectionB9Page from './pages/NsectionB9Page';
import NsectionB10Page from './pages/NsectionB10Page';
import NsectionB11Page from './pages/NsectionB11Page';
import NsectionB12Page from './pages/NsectionB12Page';
import NsectionB13Page from './pages/NsectionB13Page';
import NsectionB14Page from './pages/NsectionB14Page';
import NsectionB15Page from './pages/NsectionB15Page';
import NsectionB16Page from './pages/NsectionB16Page';
import NsectionAppendixPage from './pages/NsectionAppendixPage';
import FormulaConfigPage from './pages/FormulaConfigPage';
import MainLayout from './components/MainLayout';
import PrivateRoute from './components/PrivateRoute';

/**
 * 應用程式路由
 * 根據系統維護狀態決定顯示內容
 */
const AppRoutes: React.FC = () => {
  const { isService, isLoading } = useSystem();

  // 更新網頁 title
  useDocumentTitle();

  // 載入中
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        系統載入中...
      </div>
    );
  }

  // 系統維護中
  if (!isService) {
    return <MaintenancePage />;
  }

  // 正常服務
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/"
        element={
          <PrivateRoute>
            <MainLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/home" replace />} />
        {/* 首頁（新版） */}
        <Route path="home" element={<HomePage />} />
        {/* 儀表板（舊版，暫時保留） */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="sys_profile" element={<SysProfilePage />} />
        <Route path="user_roles" element={<UserRolesPage />} />
        <Route path="users" element={<UsersPage />} />
        {/* 系統功能管理 */}
        <Route path="system_functions" element={<SystemFunctionsPage />} />
        <Route path="org_profile" element={<div>組織資料檔案</div>} />
        <Route path="tenant_profile" element={<TenantProfilePage />} />
        <Route path="tenant_users" element={<TenantUsersPage />} />
        <Route path="role_rights" element={<RoleRightsPage />} />
        <Route path="user_logs" element={<UserLogsPage />} />
        <Route path="system_codes" element={<SystemCodesPage />} />
        <Route path="system_notifications" element={<SystemNotificationsPage />} />
        {/* 檔案附件管理 */}
        <Route path="file_attachments" element={<FileAttachmentsPage />} />
        {/* 專案基本資料 */}
        <Route path="projects" element={<ProjectsPage />} />
        {/* Section A1 - 專案目的與概述 */}
        <Route path="section_a1" element={<SectionA1Page />} />
        {/* Section A2 - 活動類型(A6.4)說明 */}
        <Route path="section_a2" element={<SectionA2Page />} />
        {/* Section A3 - 政策符合性說明 */}
        <Route path="section_a3" element={<SectionA3Page />} />
        {/* Section A4 - 專案位置 */}
        <Route path="section_a4" element={<SectionA4Page />} />
        {/* Section A5 - 技術及措施 */}
        <Route path="section_a5" element={<SectionA5Page />} />
        {/* Section A6 - 參與方 */}
        <Route path="section_a6" element={<SectionA6Page />} />
        {/* Section B1 - 方法論引用 */}
        <Route path="section_b1" element={<SectionB1Page />} />
        {/* Section B2 - 適用性 */}
        <Route path="section_b2" element={<SectionB2Page />} />
        {/* Section B3 - 專案邊界 */}
        <Route path="section_b3" element={<SectionB3Page />} />
        {/* Section B4 - 基準線情境 */}
        <Route path="section_b4" element={<SectionB4Page />} />
        {/* Section B5 - 額外性說明 */}
        <Route path="section_b5" element={<SectionB5Page />} />
        {/* Section B6 - 逆轉風險 */}
        <Route path="section_b6" element={<SectionB6Page />} />
        {/* Section B7.1 - 日常與基準計算 */}
        <Route path="section_b7_1" element={<SectionB71Page />} />
        <Route path="section_b7_2" element={<SectionB72Page />} />
        {/* Section B7.3 - 洩漏排放處理 */}
        <Route path="section_b7_3" element={<SectionB73Page />} />
        {/* Section B7.4 - 減量移除計算 */}
        <Route path="section_b7_4" element={<SectionB74Page />} />
        {/* Section B7.5 - 事前資料參數說明 */}
        <Route path="section_b7_5" element={<SectionB75Page />} />
        {/* Section B7.6 - 事前排放減量總和 */}
        <Route path="section_b7_6" element={<SectionB76Page />} />
        {/* Section B8 - 監測計畫 */}
        <Route path="section_b8" element={<SectionB8Page />} />
        {/* Section C - 計入期說明 */}
        <Route path="section_c" element={<SectionCPage />} />
        {/* Section D - 影響評估 */}
        <Route path="section_d" element={<SectionDPage />} />
        {/* Section E - 在地利害關係人諮詢 */}
        <Route path="section_e" element={<SectionEPage />} />
        {/* Section F - 重複或再次登記確認說明 */}
        <Route path="section_f" element={<SectionFPage />} />
        {/* Appendix - 附錄 */}
        <Route path="appendix" element={<AppendixPage />} />
        {/* Report - PDD 報告 */}
        <Route path="pdd_report" element={<ReportPage />} />
        {/* 方法論提案申請表 */}
        <Route path="nmeth_proposal" element={<NmethProposalPage />} />
        {/* 方法論提案申請表報告 */}
        <Route path="rpt_nmeth_proposal" element={<RptNmethProposalPage />} />
        {/* 新方法論報告 */}
        <Route path="rpt_nmeth" element={<RptNmethPage />} />
        {/* 新方法論資訊 */}
        <Route path="nmeth_info" element={<NmethInfoPage />} />
        {/* A.摘要與適用性 */}
        <Route path="nsection_a" element={<NsectionAPage />} />
        {/* B.1.說明 */}
        <Route path="nsection_b1" element={<NsectionB1Page />} />
        {/* B.2.適用範圍與生效 */}
        <Route path="nsection_b2" element={<NsectionB2Page />} />
        {/* B.3.術語定義 */}
        <Route path="nsection_b3" element={<NsectionB3Page />} />
        {/* B.4.規範性引用 */}
        <Route path="nsection_b4" element={<NsectionB4Page />} />
        {/* B.5.適用條件 */}
        <Route path="nsection_b5" element={<NsectionB5Page />} />
        {/* B.6.避免重複計量 */}
        <Route path="nsection_b6" element={<NsectionB6Page />} />
        {/* B.7.政策、選項與實施計畫符合性說明 */}
        <Route path="nsection_b7" element={<NsectionB7Page />} />
        {/* B.8.活動邊界 */}
        <Route path="nsection_b8" element={<NsectionB8Page />} />
        {/* B.9.額外性的證明 */}
        <Route path="nsection_b9" element={<NsectionB9Page />} />
        {/* B.10.基準情景 */}
        <Route path="nsection_b10" element={<NsectionB10Page />} />
        {/* B.11.活動情景 */}
        <Route path="nsection_b11" element={<NsectionB11Page />} />
        {/* B.12.洩漏 */}
        <Route path="nsection_b12" element={<NsectionB12Page />} />
        {/* B.13.減排 */}
        <Route path="nsection_b13" element={<NsectionB13Page />} />
        {/* B.14.事前參數說明 */}
        <Route path="nsection_b14" element={<NsectionB14Page />} />
        {/* B.15.監控參數說明 */}
        <Route path="nsection_b15" element={<NsectionB15Page />} />
        {/* B.16.方法論原則 */}
        <Route path="nsection_b16" element={<NsectionB16Page />} />
        {/* 附錄 */}
        <Route path="nsection_appendix" element={<NsectionAppendixPage />} />
        {/* 方程式設定 */}
        <Route path="formula_config" element={<FormulaConfigPage />} />
        {/* 個人資料變更 */}
        <Route path="my_profile" element={<MyProfilePage />} />
        {/* 密碼變更 */}
        <Route path="change_password" element={<ChangePasswordPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/**
 * 應用程式主元件
 */
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <SystemProvider>
        <AuthProvider>
          <ProjectProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </ProjectProvider>
        </AuthProvider>
      </SystemProvider>
    </BrowserRouter>
  );
};

export default App;
